import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { useCookieConsent } from '../hooks/useCookieConsent';
import CookiePreferencesModal from './CookiePreferencesModal';

const CookieConsentBanner: React.FC = () => {
  const { shouldShowBanner, acceptAll, rejectNonEssential, savePreferences, consent } =
    useCookieConsent();
  const [showModal, setShowModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (shouldShowBanner) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setIsAnimating(true));
        });
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setIsAnimating(false);
    }
  }, [shouldShowBanner]);

  if (!shouldShowBanner || !isVisible) return null;

  return (
    <>
      {/* Subtle backdrop */}
      <div
        className={`fixed inset-0 bg-black/5 z-[9997] transition-opacity duration-500 pointer-events-none ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[9998] p-4 sm:p-6 transition-all duration-500 ease-out ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/80 shadow-2xl p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Icon and Text */}
              <div className="flex items-start gap-3.5 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center border border-amber-100">
                  <Cookie className="h-5 w-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    We value your privacy
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
                    We use cookies to enhance your experience, remember your preferences, and
                    analyze site usage. You can customize your preferences or accept all
                    cookies.{' '}
                    <Link
                      to="/cookies"
                      className="text-blue-600 hover:text-blue-500 underline underline-offset-2"
                    >
                      Cookie Policy
                    </Link>
                    {' · '}
                    <Link
                      to="/privacy"
                      className="text-blue-600 hover:text-blue-500 underline underline-offset-2"
                    >
                      Privacy Policy
                    </Link>
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={rejectNonEssential}
                  className="flex-1 sm:flex-initial px-3.5 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
                >
                  Reject
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="flex-1 sm:flex-initial px-3.5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors whitespace-nowrap border border-gray-200"
                >
                  Customize
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 sm:flex-initial px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showModal && (
        <CookiePreferencesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={(functional, analytics) => {
            savePreferences(functional, analytics);
            setShowModal(false);
          }}
          initialFunctional={consent.categories.functional}
          initialAnalytics={consent.categories.analytics}
        />
      )}
    </>
  );
};

export default CookieConsentBanner;
